import { useState, useEffect, useRef } from 'react';
import { cn } from '../../lib/cn';
import { Textarea } from '../ui';

export default function ShortAnswer({
  value,
  onChange,
  placeholder = 'Type your answer here...',
  minRows = 3,
  maxRows = 8,
  disabled = false,
  className,
}) {
  const textareaRef = useRef(null);
  const [rows, setRows] = useState(minRows);

  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to calculate new height
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const lineHeight = parseInt(getComputedStyle(textareaRef.current).lineHeight);
      const newRows = Math.min(
        Math.max(Math.ceil(scrollHeight / lineHeight), minRows),
        maxRows
      );
      setRows(newRows);
    }
  }, [value, minRows, maxRows]);

  return (
    <div className={cn('relative', className)}>
      <Textarea
        ref={textareaRef}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={cn(
          'resize-none min-h-[100px]',
          'focus:ring-0'
        )}
      />
      {value && value.length > 0 && (
        <div className="absolute bottom-3 right-3 text-xs text-gray-400 dark:text-gray-500">
          {value.length} characters
        </div>
      )}
    </div>
  );
}

export function ShortAnswerWithWordCount({
  value,
  onChange,
  placeholder = 'Type your answer here...',
  minWords,
  maxWords,
  disabled = false,
  className,
}) {
  const wordCount = value ? value.trim().split(/\s+/).filter(Boolean).length : 0;

  const getWordCountColor = () => {
    if (!minWords && !maxWords) return 'text-gray-400 dark:text-gray-500';
    if (minWords && wordCount < minWords) return 'text-amber-500';
    if (maxWords && wordCount > maxWords) return 'text-red-500';
    return 'text-green-600 dark:text-green-400';
  };

  const getWordCountText = () => {
    if (minWords && maxWords) {
      return `${wordCount} / ${minWords}-${maxWords} words`;
    }
    if (minWords) {
      return `${wordCount} / ${minWords}+ words`;
    }
    if (maxWords) {
      return `${wordCount} / ${maxWords} words max`;
    }
    return `${wordCount} words`;
  };

  return (
    <div className={className}>
      <ShortAnswer
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
      />
      <div className={cn('mt-2 text-sm text-right', getWordCountColor())}>
        {getWordCountText()}
      </div>
    </div>
  );
}
